import { convertDistance } from 'geolib';
import { useCallback, useEffect, useState } from 'react';
import usePlacesAutocomplete, { getGeocode } from 'use-places-autocomplete';

import { useGoogleApi } from '@goodfynd/react-web.context.google-api-context';

import { unicode } from '../config';
import * as geoUtil from '../utils/geo-util';

export const useGeoInput = (initialValue?: JsonLocation, lookup = true) => {
  const { isLoaded: googleApiIsLoaded } = useGoogleApi();

  const [location, setLocation] = useState<JsonLocation | undefined>(
    initialValue
  );

  const {
    init,
    ready,
    value,
    suggestions: { data },
    setValue,
    clearSuggestions,
  } = usePlacesAutocomplete({
    debounce: 300,
    initOnMount: false,
    requestOptions:
      global.window?.google &&
      initialValue &&
      Boolean(initialValue?.latitude) &&
      Boolean(initialValue?.longitude)
        ? {
            bounds: new google.maps.LatLngBounds({
              lat: initialValue.latitude,
              lng: initialValue.longitude,
            }),
            origin: new google.maps.LatLng({
              lat: initialValue.latitude,
              lng: initialValue.longitude,
            }),
          }
        : undefined,
  });

  const suggestions =
    data.map(
      ({
        distance_meters,
        place_id,
        structured_formatting: { main_text, secondary_text },
      }) => ({
        id: place_id,
        label: main_text,
        subLabel: [
          secondary_text,
          distance_meters && distance_meters > 0
            ? `${convertDistance(distance_meters, 'mi').toFixed(1)} mi.`
            : undefined,
        ]
          .filter(Boolean)
          .join(unicode.centerDotPadded),
        value: place_id,
      })
    ) || [];

  const selectLocation = useCallback(
    async (option?: SelectOption): Promise<JsonLocation | undefined> => {
      clearSuggestions();

      if (!option) {
        setValue('', false);
        setLocation(undefined);
        return;
      }

      const { id, label, name } = option;
      setValue(label, false);
      try {
        const results = await getGeocode({
          address: id ? '' : label,
          placeId: id,
        });

        const { formatted_address } = results[0];
        setValue(formatted_address, false);
        const location = { ...geoUtil.mapGeoResult(results[0]), name };
        setLocation(location);
        return location;
      } catch (error) {
        console.error('😱 Error: ', error);
      }

      return;
    },
    [clearSuggestions, setValue]
  );

  useEffect(() => {
    if (!googleApiIsLoaded) {
      return;
    }

    init();

    if (initialValue) {
      setValue(initialValue.address, false);
      if (
        lookup &&
        (!initialValue.city ||
          !initialValue.state ||
          !initialValue.county ||
          initialValue.coordinates === '0,0')
      ) {
        // finds missing coordinates, city , state, and county fields
        selectLocation({ label: initialValue.address, value: '' });
      }
    }
  }, [googleApiIsLoaded, init, initialValue, lookup, selectLocation, setValue]);

  const setLatLng = ({ lat, lng }: google.maps.LatLngLiteral) => {
    getGeocode({ location: { lat, lng } })
      .then((results) => {
        const { formatted_address } = results[0];
        setValue(formatted_address, false);
        setLocation(geoUtil.mapGeoResult(results[0]));
      })
      .catch((error) => {
        console.error('😱 Error: ', error);
      });
  };

  const setLocationName = (name?: string) => {
    setLocation(
      (curLocation) =>
        curLocation && {
          ...curLocation,
          name,
        }
    );
  };

  return {
    location,
    selectLocation,
    setLocationName,
    query: value,
    setQuery: setValue,
    suggestions,
    ready,
    setLatLng,
  };
};
